/**
 * Version-based cache management for PWA
 * Ensures clean state when deploying new versions by clearing all client storage
 */

const VERSION_KEY = 'app_version';
const DEPLOYED_CLEAR_GUARD_KEY = 'app_version_last_cleared_deployed';
const CURRENT_VERSION = '0.8.9'; // Should match package.json

export interface ClearOptions {
  clearServiceWorkers?: boolean;
  clearCaches?: boolean;
  clearIndexedDB?: boolean;
  clearLocalStorage?: boolean;
  clearSessionStorage?: boolean;
}

export interface DeployedVersionCheckResult {
  didCheck: boolean;
  wasCleared: boolean;
}

/**
 * Get the stored app version from localStorage
 */
export function getStoredVersion(): string | null {
  try {
    return localStorage.getItem(VERSION_KEY);
  } catch (e) {
    console.warn('Failed to read stored version:', e);
    return null;
  }
}

/**
 * Store the current app version in localStorage
 */
export function setStoredVersion(version: string = CURRENT_VERSION): void {
  try {
    localStorage.setItem(VERSION_KEY, version);
  } catch (e) {
    console.warn('Failed to store version:', e);
  }
}

function getLastClearedDeployedVersion(): string | null {
  try {
    return localStorage.getItem(DEPLOYED_CLEAR_GUARD_KEY);
  } catch (e) {
    console.warn('Failed to read deployed clear guard version:', e);
    return null;
  }
}

function setLastClearedDeployedVersion(version: string): void {
  try {
    localStorage.setItem(DEPLOYED_CLEAR_GUARD_KEY, version);
  } catch (e) {
    console.warn('Failed to store deployed clear guard version:', e);
  }
}

/**
 * Check if app version has changed (new deployment)
 */
export function hasVersionChanged(): boolean {
  const stored = getStoredVersion();
  return stored !== null && stored !== CURRENT_VERSION;
}

async function withTimeout<T>(
  p: Promise<T>,
  label: string,
  ms = 8000,
): Promise<T> {
  return Promise.race([
    p,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`timeout:${label}`)), ms),
    ),
  ]);
}

export async function clearAllStorage(opts: {
  clearServiceWorkers?: boolean;
  clearCaches?: boolean;
  clearIndexedDB?: boolean;
  clearLocalStorage?: boolean;
  clearSessionStorage?: boolean;
}): Promise<void> {
  console.log('[clear] start', opts);

  if (opts.clearServiceWorkers) {
    try {
      if (!('serviceWorker' in navigator)) {
        console.log(
          '[clear] serviceWorker API not available, skipping SW cleanup',
        );
      } else {
        const reg = await withTimeout(
          navigator.serviceWorker.ready,
          'sw-ready',
          2500,
        ).catch(() => null);
        if (reg?.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        const regs = await withTimeout(
          navigator.serviceWorker.getRegistrations(),
          'sw-get-registrations',
          2500,
        ).catch(() => [] as ServiceWorkerRegistration[]);
        console.log('[clear] unregister SW count', regs.length);
        await withTimeout(
          Promise.allSettled(regs.map((r) => r.unregister())),
          'sw-unregister',
        );
      }
    } catch (e) {
      console.error('[clear] sw error', e);
    }
  }

  if (opts.clearCaches && 'caches' in window) {
    try {
      const keys = await caches.keys();
      console.log('[clear] caches keys', keys);
      await withTimeout(
        Promise.allSettled(keys.map((k) => caches.delete(k))),
        'caches-delete',
      );
    } catch (e) {
      console.error('[clear] caches error', e);
    }
  }

  if (opts.clearIndexedDB) {
    try {
      // List known DBs to delete
      const dbs = ['pinia-orm', 'workbox-precache', 'workbox-expiration']; // adjust names
      console.log('[clear] idb dbs', dbs);
      await withTimeout(
        Promise.allSettled(
          dbs.map(
            (name) =>
              new Promise<void>((resolve) => {
                const req = indexedDB.deleteDatabase(name);
                req.onsuccess = () => resolve();
                req.onerror = () => resolve(); // don’t block
                req.onblocked = () => resolve();
              }),
          ),
        ),
        'idb-delete',
      );
    } catch (e) {
      console.error('[clear] idb error', e);
    }
  }

  if (opts.clearLocalStorage) {
    try {
      console.log('[clear] localStorage size', localStorage.length);
      localStorage.clear();
    } catch (e) {
      console.error('[clear] ls error', e);
    }
  }

  if (opts.clearSessionStorage) {
    try {
      console.log('[clear] sessionStorage size', sessionStorage.length);
      sessionStorage.clear();
    } catch (e) {
      console.error('[clear] ss error', e);
    }
  }

  const est = await navigator.storage?.estimate?.().catch(() => null);
  console.log('[clear] storage estimate', est);
  console.log('[clear] done');
}

/**
 * Check version and auto-clear storage if version changed
 * Returns true if storage was cleared
 */
export async function checkVersionAndClearIfNeeded(): Promise<boolean> {
  if (hasVersionChanged()) {
    const oldVersion = getStoredVersion();
    console.warn(
      `App version changed from ${oldVersion} to ${CURRENT_VERSION}. Clearing storage...`,
    );

    await clearAllStorage({
      clearServiceWorkers: true,
      clearCaches: true,
      clearIndexedDB: true,
      clearLocalStorage: false, // Keep auth token to avoid forced re-login
      clearSessionStorage: true,
    });

    // Update to current version
    setStoredVersion(CURRENT_VERSION);

    return true;
  }

  // First install - store version
  if (getStoredVersion() === null) {
    setStoredVersion(CURRENT_VERSION);
  }

  return false;
}

/**
 * Fetch deployed app version from manifest and clear storage if it differs
 * from locally stored version. This works even when current JS bundle is stale.
 */
export async function checkDeployedVersionAndClearIfNeeded(): Promise<DeployedVersionCheckResult> {
  try {
    const manifestUrl = `/manifest.json?v=${Date.now()}`;
    const response = await fetch(manifestUrl, {
      cache: 'no-store',
      headers: {
        pragma: 'no-cache',
        'cache-control': 'no-cache',
      },
    });

    if (!response.ok) {
      return { didCheck: false, wasCleared: false };
    }

    const manifest = (await response.json()) as { version?: string };
    const deployedVersion =
      typeof manifest?.version === 'string' ? manifest.version : null;

    if (!deployedVersion) {
      return { didCheck: false, wasCleared: false };
    }

    const stored = getStoredVersion();
    const lastClearedDeployedVersion = getLastClearedDeployedVersion();

    // Guard against repeated clears on refresh for the same deployed version.
    // This can happen when an older bundle temporarily rewrites app_version.
    if (
      stored &&
      stored !== deployedVersion &&
      lastClearedDeployedVersion === deployedVersion
    ) {
      setStoredVersion(deployedVersion);
      return { didCheck: true, wasCleared: false };
    }

    if (stored && stored !== deployedVersion) {
      console.warn(
        `Deployed app version changed from ${stored} to ${deployedVersion}. Clearing storage...`,
      );

      await clearAllStorage({
        clearServiceWorkers: true,
        clearCaches: true,
        clearIndexedDB: true,
        clearLocalStorage: false,
        clearSessionStorage: true,
      });

      setLastClearedDeployedVersion(deployedVersion);
      setStoredVersion(deployedVersion);
      return { didCheck: true, wasCleared: true };
    }

    if (stored === null) {
      setLastClearedDeployedVersion(deployedVersion);
      setStoredVersion(deployedVersion);
    }

    return { didCheck: true, wasCleared: false };
  } catch (e) {
    console.warn('Failed to check deployed manifest version:', e);
    return { didCheck: false, wasCleared: false };
  }
}
